import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="flash-messages"
// Unified flash message controller that handles both auto-dismiss and manual dismiss
export default class extends Controller {
  static targets = ["message", "progressBar"]
  static values = { 
    duration: { type: Number, default: 5000 },
    autoDismiss: { type: Boolean, default: true }
  }

  connect() {
    if (this.autoDismissValue) {
      this.startAutoHide()
    }
  }

  startAutoHide() {
    this.messageTargets.forEach((message, index) => {
      const progressBar = this.progressBarTargets[index]
      if (progressBar) {
        // Start the progress bar animation
        setTimeout(() => {
          progressBar.style.transform = "scaleX(0)"
        }, 100)
      }

      // Auto-hide after duration
      setTimeout(() => {
        this.hideMessage(message)
      }, this.durationValue)
    })
  }

  dismiss(event) {
    const message = event.currentTarget.closest('[data-flash-messages-target="message"]')
    if (message) {
      this.hideMessage(message)
    }
  }

  hideMessage(message) {
    // Add hiding animation
    message.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
    message.style.opacity = "0"
    message.style.transform = "translateX(100%)"
    
    // Remove from DOM after animation
    setTimeout(() => {
      if (message.parentNode) {
        message.remove()
      }
    }, 300)
  }

  // Method to add new flash messages dynamically
  addMessage(type, text) {
    const container = this.element.querySelector('#flash-messages') || this.element
    if (!container) return

    const messageHtml = this.createMessageHTML(type, text)
    container.insertAdjacentHTML('beforeend', messageHtml)
    
    // Start auto-hide for the new message
    const newMessage = container.lastElementChild
    const progressBar = newMessage.querySelector('[data-flash-messages-target="progressBar"]')
    
    if (progressBar) {
      setTimeout(() => {
        progressBar.style.transform = "scaleX(0)"
      }, 100)
    }

    if (this.autoDismissValue) {
      setTimeout(() => {
        this.hideMessage(newMessage)
      }, this.durationValue)
    }
  }

  createMessageHTML(type, text) {
    const icons = {
      success: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      error: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      alert: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      notice: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      warning: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg>'
    }

    const classes = {
      success: 'backdrop-blur-md bg-opacity-95 border bg-green-900/90 border-green-500/50 text-green-100',
      error: 'backdrop-blur-md bg-opacity-95 border bg-red-900/90 border-red-500/50 text-red-100',
      alert: 'backdrop-blur-md bg-opacity-95 border bg-red-900/90 border-red-500/50 text-red-100',
      notice: 'backdrop-blur-md bg-opacity-95 border bg-green-900/90 border-green-500/50 text-green-100',
      warning: 'backdrop-blur-md bg-opacity-95 border bg-yellow-900/90 border-yellow-500/50 text-yellow-100'
    }

    const dismissClasses = {
      success: 'text-green-300 hover:text-green-100 hover:bg-green-800/50 focus:ring-green-500',
      error: 'text-red-300 hover:text-red-100 hover:bg-red-800/50 focus:ring-red-500',
      alert: 'text-red-300 hover:text-red-100 hover:bg-red-800/50 focus:ring-red-500',
      notice: 'text-green-300 hover:text-green-100 hover:bg-green-800/50 focus:ring-green-500',
      warning: 'text-yellow-300 hover:text-yellow-100 hover:bg-yellow-800/50 focus:ring-yellow-500'
    }

    return `
      <div data-flash-messages-target="message" 
           class="${classes[type] || classes.notice} rounded-lg shadow-lg overflow-hidden transition-all duration-300 transform translate-x-0 opacity-100"
           data-message-type="${type}">
        <div class="px-4 py-3">
          <div class="flex items-start">
            <div class="flex-shrink-0 mt-0.5">
              ${icons[type] || icons.notice}
            </div>
            <div class="ml-3 flex-1">
              <p class="text-sm font-medium">${text}</p>
            </div>
            <div class="ml-4 flex-shrink-0">
              <button type="button" 
                      data-action="flash-messages#dismiss" 
                      class="inline-flex rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent ${dismissClasses[type] || dismissClasses.notice}">
                <span class="sr-only">Dismiss</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        ${this.autoDismissValue ? `
        <div class="h-1 bg-black/20">
          <div data-flash-messages-target="progressBar" 
               class="h-full bg-current opacity-50 transition-transform duration-[${this.durationValue}ms] ease-linear transform scale-x-100 origin-left"></div>
        </div>
        ` : ''}
      </div>
    `
  }
}
